/** Image helpers shared by the tools that read scanned pages with OCR. */

const DARK = 110;

/**
 * Whites out thin, long ruled lines (table borders) in a grayscale picture, in place. A letter that touches
 * a vertical rule is otherwise read as part of the line and its first character is lost (PX-12, "Interest" -> "nterest").
 * A run counts as a rule only when it is at least `minLen` px long and has light pixels a few px to each side,
 * so photographs and filled areas are left alone.
 */
export function removeRuledLines(gray: Uint8ClampedArray, w: number, h: number, minLen = Math.max(40, Math.round(w / 25))): void {
  const at = (x: number, y: number) => gray[(y * w + x) * 4];
  const wipe: number[] = [];
  const side = 5;
  // vertical rules
  for (let x = side; x < w - side; x++) {
    let y = 0;
    while (y < h) {
      if (at(x, y) >= DARK) { y++; continue; }
      const y0 = y;
      while (y < h && at(x, y) < DARK) y++;
      if (y - y0 >= minLen) {
        let clear = 0;
        for (let k = y0; k < y; k++) if (at(x - side, k) >= DARK && at(x + side, k) >= DARK) clear++;
        if (clear >= 0.8 * (y - y0)) for (let k = y0; k < y; k++) wipe.push(k * w + x);
      }
    }
  }
  // horizontal rules
  for (let y = side; y < h - side; y++) {
    let x = 0;
    while (x < w) {
      if (at(x, y) >= DARK) { x++; continue; }
      const x0 = x;
      while (x < w && at(x, y) < DARK) x++;
      if (x - x0 >= minLen) {
        let clear = 0;
        for (let k = x0; k < x; k++) if (at(k, y - side) >= DARK && at(k, y + side) >= DARK) clear++;
        if (clear >= 0.8 * (x - x0)) for (let k = x0; k < x; k++) wipe.push(y * w + k);
      }
    }
  }
  for (const idx of wipe) {
    const o = idx * 4;
    gray[o] = gray[o + 1] = gray[o + 2] = 255;
  }
}

/** Light preprocessing before OCR: grayscale plus a modest contrast boost, with table rules removed. */
export async function preprocessForOcr(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return blob;
  ctx.drawImage(bitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  const contrast = 1.15;
  for (let i = 0; i < d.length; i += 4) {
    const gray = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
    d[i] = d[i + 1] = d[i + 2] = Math.min(255, Math.max(0, (gray - 128) * contrast + 128));
  }
  removeRuledLines(d, canvas.width, canvas.height);
  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Preprocessing failed"))), "image/jpeg", 0.92));
}

/** The image turned clockwise by 90, 180 or 270 degrees. */
export async function rotateImageBlob(blob: Blob, degrees: 90 | 180 | 270): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  const swap = degrees !== 180;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return blob;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Rotation failed"))), "image/jpeg", 0.92));
}
